<!--
CercleDigital InsiderGems, gestion électronique documentaire partagée de l'écosystème Cercle.
Copyright 2026 Pierre-Thibault Fabre <pierre-thibault@cercle.digital>
SPDX-License-Identifier: LicenseRef-Cercle-Interne

Fichier : apps/web/src/shell/BarreLaterale.vue
Rôle : navigation principale, par catégories, liens techniques et mention
       de révision en pied.
Référence : designsystem.md ; CIG-SPECIFICATION.md §5.1, §22.
-->
<script setup lang="ts">
import { computed } from 'vue';
import { useSession } from '../lib/store.ts';
import { useNavigation } from '../lib/navigation.ts';
import Rouet from './Rouet.vue';

const session = useSession();
const navigation = useNavigation();
const emit = defineEmits<{ naviguer: [] }>();

interface Entree {
  to: string;
  libelle: string;
  icone: string;
  visible: boolean;
  /** Décompte affiché en fin de ligne ; nul, il n'est pas rendu. */
  cartouche?: number;
}

/**
 * Catégories de navigation. Les entrées invisibles ne sont pas rendues : un
 * utilisateur ne voit jamais une action qu'il n'a pas le droit de faire
 * (spécification §5.1 point 4).
 */
const categories = computed<{ titre: string; entrees: Entree[] }[]>(() => [
  {
    titre: 'Documents',
    entrees: [
      { to: '/', libelle: 'Explorateur', icone: 'dossier', visible: true },
      { to: '/recents', libelle: 'Récents', icone: 'horloge', visible: true },
      { to: '/partage-avec-moi', libelle: 'Partagé avec moi', icone: 'partage', visible: true },
      // Une corbeille pleine se signale : son décompte est porté en fin de
      // ligne et l'icône devient pleine, visible d'un coup d'œil.
      { to: '/corbeille', libelle: 'Corbeille', icone: 'corbeille', visible: true, cartouche: session.corbeille },
    ],
  },
  {
    titre: 'Organisation',
    entrees: [
      { to: '/collections', libelle: 'Collections', icone: 'collection', visible: true },
      { to: '/modeles', libelle: 'Modèles', icone: 'modele', visible: true },
    ],
  },
  {
    titre: 'Validation',
    entrees: [
      { to: '/circuits', libelle: 'Circuits', icone: 'circuit', visible: true },
      // Les revues sont une charge de propriétaire, pas une fonction
      // d'exploitation : leur place est dans la navigation ordinaire, avec un
      // décompte de ce qui reste à trancher (spécification §18.8).
      {
        to: '/revues',
        libelle: 'Revues d\'accès',
        icone: 'revue',
        visible: true,
        cartouche: session.revues,
      },
    ],
  },
].map((c) => ({ ...c, entrees: c.entrees.filter((e) => e.visible) }))
  .filter((c) => c.entrees.length > 0));

const techniques = computed<Entree[]>(() => {
  const admin = session.profil?.isAdmin === true;
  return [
    { to: '/favoris', libelle: 'Favoris', icone: 'etoile', visible: true },
    { to: '/partages', libelle: 'Partages', icone: 'lien', visible: session.peut('links.create') || admin },
    { to: '/jetons', libelle: 'Jetons', icone: 'cle', visible: session.peut('tokens.create') || admin },
    { to: '/admin', libelle: 'Administration', icone: 'reglage', visible: admin },
  ].filter((e) => e.visible);
});

/**
 * Mention de révision. L'empreinte du commit est figée à la construction : un
 * rapport d'anomalie désigne ainsi sans ambiguïté la version servie.
 */
const commit = __CIG_COMMIT__;
const annee = new Date().getFullYear();
</script>

<template>
  <nav id="navigation-laterale" class="laterale" aria-label="Navigation principale">
    <div class="corps">
      <section v-for="c in categories" :key="c.titre" class="categorie">
        <h2 class="titre">{{ c.titre }}</h2>
        <ul class="entrees">
          <li v-for="e in c.entrees" :key="e.to">
            <RouterLink :to="e.to" class="entree" @click="emit('naviguer')">
              <svg class="icone" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                <template v-if="e.icone === 'dossier'">
                  <path d="M2 5.5A1.5 1.5 0 0 1 3.5 4h4l1.6 1.7h7.4A1.5 1.5 0 0 1 18 7.2v8.3a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 2 15.5v-10Z" />
                </template>
                <template v-else-if="e.icone === 'horloge'">
                  <circle cx="10" cy="10" r="7.2" />
                  <path d="M10 5.8V10l3 1.8" class="trait" />
                </template>
                <template v-else-if="e.icone === 'partage'">
                  <circle cx="6" cy="10" r="2.4" />
                  <circle cx="14.5" cy="5.5" r="2.4" />
                  <circle cx="14.5" cy="14.5" r="2.4" />
                  <path d="M8.2 8.9 12.4 6.7M8.2 11.1l4.2 2.2" class="trait" />
                </template>
                <template v-else-if="e.icone === 'corbeille'">
                  <!-- Corbeille pleine : le contenant est rempli, la silhouette
                       reste identique. -->
                  <path
                    d="M6 6l.7 10.4h6.6L14 6"
                    :fill="e.cartouche ? 'currentColor' : 'none'"
                    :stroke-width="e.cartouche ? 0 : 1.5"
                  />
                  <path d="M4.5 6h11M8 6V4.6h4V6" />
                </template>
                <template v-else-if="e.icone === 'collection'">
                  <path d="M3 6h8v9H3zM8.5 4h8v9" />
                </template>
                <template v-else-if="e.icone === 'modele'">
                  <path d="M5 3.5h6.5L15 7v9.5H5z" />
                  <path d="M11.5 3.5V7H15" class="trait" />
                </template>
                <template v-else-if="e.icone === 'revue'">
                  <!-- Une liste que l'on coche : le geste même de la revue. -->
                  <path d="M4 5.5h9M4 10h9M4 14.5h6" class="trait" />
                  <path d="m14.2 13.6 1.5 1.6 2.6-3.2" class="trait" />
                </template>
                <template v-else>
                  <circle cx="5" cy="6" r="2" />
                  <circle cx="15" cy="14" r="2" />
                  <path d="M5 8v6.5h8" class="trait" />
                </template>
              </svg>
              <span class="libelle">{{ e.libelle }}</span>
              <!-- Rouet en ligne : l'attente se marque là où l'on a cliqué. -->
              <Rouet v-if="navigation.cible === e.to" :taille="15" :libelle="`Ouverture de ${e.libelle}`" class="rouet" />
              <span
                v-else-if="e.cartouche"
                class="cartouche"
                :aria-label="`${e.cartouche} objet${e.cartouche > 1 ? 's' : ''} en corbeille`"
              >{{ e.cartouche > 99 ? '99+' : e.cartouche }}</span>
            </RouterLink>
          </li>
        </ul>
      </section>
    </div>

    <!-- Liens techniques : empilés, en petit, nettement détachés du haut. -->
    <div class="pied">
      <ul class="techniques">
        <li v-for="e in techniques" :key="e.to">
          <RouterLink :to="e.to" class="technique" @click="emit('naviguer')">
            <svg class="icone-technique" viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
              <template v-if="e.icone === 'etoile'">
                <path d="m10 3 2.1 4.4 4.7.6-3.4 3.3.9 4.7L10 13.8 5.7 16l.9-4.7-3.4-3.3 4.7-.6z" />
              </template>
              <template v-else-if="e.icone === 'lien'">
                <path d="M8.4 11.6a3.2 3.2 0 0 0 4.6 0l2.4-2.4a3.2 3.2 0 0 0-4.6-4.6l-1 1" />
                <path d="M11.6 8.4a3.2 3.2 0 0 0-4.6 0l-2.4 2.4a3.2 3.2 0 0 0 4.6 4.6l1-1" />
              </template>
              <template v-else-if="e.icone === 'cle'">
                <circle cx="7" cy="7" r="3.2" />
                <path d="m9.3 9.3 6.2 6.2M13 13l1.6-1.6M15 15l1.4-1.4" />
              </template>
              <template v-else>
                <circle cx="10" cy="10" r="2.6" />
                <path d="M10 2.6v2M10 15.4v2M2.6 10h2M15.4 10h2M4.8 4.8l1.4 1.4M13.8 13.8l1.4 1.4M15.2 4.8l-1.4 1.4M6.2 13.8l-1.4 1.4" />
              </template>
            </svg>
            <span class="libelle">{{ e.libelle }}</span>
            <Rouet v-if="navigation.cible === e.to" :taille="13" :libelle="`Ouverture de ${e.libelle}`" class="rouet" />
          </RouterLink>
        </li>
      </ul>

      <!-- Mention de révision : discrète, mais toujours citable. -->
      <p class="mention">
        © {{ annee }} Cercle Digital
        <span class="commit" :title="`Révision servie : ${commit}`">{{ commit }}</span>
      </p>
    </div>
  </nav>
</template>

<style scoped>
/*
 * La barre latérale reprend la couleur de contraste de l'en-tête : la coque
 * forme un seul bloc sombre, le contenu documentaire reste seul en clair.
 */
.laterale {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--cig-contraste);
  color: var(--cig-contraste-encre);
  padding: var(--cig-espace-3) var(--cig-espace-2) var(--cig-espace-2);
  overflow-y: auto;
}

.corps { flex: 1; }
.categorie + .categorie { margin-top: var(--cig-espace-4); }

.titre {
  font-family: var(--cig-sans);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--cig-contraste-gris);
  margin: 0 0 var(--cig-espace-1) var(--cig-espace-2);
}

.entrees, .techniques { list-style: none; margin: 0; padding: 0; }

.entree {
  display: flex;
  align-items: center;
  gap: var(--cig-espace-2);
  min-height: var(--cig-touche);
  padding: 0 var(--cig-espace-2);
  border-radius: var(--cig-rayon-petit);
  font-family: var(--cig-sans);
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--cig-contraste-encre);
  text-decoration: none;
}
.entree:hover { background: var(--cig-contraste-survol); }
.entree.router-link-active { background: var(--cig-contraste-actif); font-weight: 600; }

.libelle { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* Les icônes sont blanches, au même trait que le texte qu'elles accompagnent. */
.icone { flex: none; fill: none; stroke: currentColor; stroke-width: 1.5; stroke-linejoin: round; }
.icone .trait { fill: none; }

/* Le rouet prend la place du décompte : la ligne ne bouge pas. */
.rouet { flex: none; color: var(--cig-contraste-encre); }

/* Décompte en fin de ligne : lisible, sans couleur d'alarme. */
.cartouche {
  flex: none;
  min-width: 1.35rem;
  padding: 0 0.35rem;
  border-radius: var(--cig-rayon-pilule);
  background: var(--cig-contraste-encre);
  color: var(--cig-contraste);
  font-family: var(--cig-sans);
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1.35rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

/*
 * Les liens techniques sont nettement détachés de la navigation documentaire :
 * un filet et une marge généreuse suffisent, sans bordure appuyée.
 */
.pied {
  margin-top: var(--cig-espace-3);
  padding-top: var(--cig-espace-2);
  border-top: 1px solid var(--cig-contraste-filet);
}

/* Empilés, un par ligne. */
.techniques { display: flex; flex-direction: column; gap: 1px; }

/* Petits et discrets, mais atteignables au doigt sur tactile. */
.technique {
  display: flex;
  align-items: center;
  gap: var(--cig-espace-2);
  min-height: 32px;
  padding: 0 var(--cig-espace-2);
  border-radius: var(--cig-rayon-petit);
  font-family: var(--cig-sans);
  font-size: 0.76rem;
  font-weight: 500;
  color: var(--cig-contraste-gris);
  text-decoration: none;
}
.technique .libelle { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.technique:hover { background: var(--cig-contraste-survol); color: var(--cig-contraste-encre); }
.technique.router-link-active {
  color: var(--cig-contraste-encre);
  font-weight: 600;
  background: var(--cig-contraste-survol);
}

.icone-technique {
  flex: none;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* Mention de révision : le plus discret de la barre, jamais illisible. */
.mention {
  margin: var(--cig-espace-2) 0 0;
  padding: 0 var(--cig-espace-2);
  font-family: var(--cig-sans);
  font-size: 0.64rem;
  line-height: 1.5;
  color: var(--cig-contraste-gris);
}
.commit {
  display: inline-block;
  margin-left: 0.3rem;
  font-variant-numeric: tabular-nums;
  opacity: 0.8;
  user-select: text;
}
</style>
